import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  buildBreakdown,
  GenerationNotFoundError,
  itemStatusView,
  skipReasonKey,
  tokenLine,
  useGenerationQuery,
  type BreakdownItem,
  type BreakdownSection,
  type BreakdownSense,
  type GenerationDetail,
  type GenerationSummary,
} from '@entities/Generation';
import { trackEvent } from '@shared/analytics';
import { buildUnitPath } from '@shared/config';
import { pageList, type PageWindow } from '@shared/lib';
import { TokenIcon } from '@shared/ui';
import { ItemLine, ReenterButton, type ItemActionsContext } from './ItemLine';
import { Bar, SenseLine } from './SenseLine';

/** Порции (правило владельца «второстепенное — порциями», § 22.5): переводы — 3 и затем все остальные,
 *  слова, фразы и фразовые глаголы — 6 и «ещё» по 10, пропуски — 3 и «ещё» по 10. */
const SENSES_WINDOW: PageWindow = { base: 3, step: Infinity };
const ITEMS_WINDOW: PageWindow = { base: 6, step: 10 };
const SKIPPED_WINDOW: PageWindow = { base: 3, step: 10 };

/** Через сколько уходит запись, которой больше нет (404, модуль удалён). */
const GONE_DELAY_MS = 3000;

/**
 * Строки, которые порция не прячет (правило «статус и ошибка — рядом с тем, к чему относятся»): иначе при
 * четырёх и больше значениях шапка пишет «не получилось», а в раскрытой записи видны только готовые переводы —
 * ни причины, ни выхода. Значение — «не добавилось» или новое значение, которое дописывается; элемент —
 * со статусом, по которому нужно действовать (не получилось, опечатка, не распознано, не добавилось).
 * Порядок строк — серверный, как в листе словаря.
 */
const senseNeedsEye = (entry: BreakdownItem, sense: BreakdownSense) =>
  sense.status === 'failed' || (sense.status === 'generating' && entry.item.status === 'extending');
const ITEM_ATTENTION = new Set<BreakdownItem['item']['status']>(['failed', 'typo', 'garbage', 'selection_failed']);
const itemNeedsEye = (entry: BreakdownItem) =>
  ITEM_ATTENTION.has(entry.item.status) || (entry.item.status === 'ready' && entry.failedPicks.length > 0);

export interface GenerationBreakdownProps {
  summary: GenerationSummary;
  now: number;
  /** Откуда считать «дольше обычного» у элемента (§ 22.4). */
  itemSlowSince: (itemId: string) => number | null;
  ctx: ItemActionsContext;
  /** «Свернуть» внизу записи. */
  onCollapse: () => void;
  /** 404: записи больше нет — через 3 с она уходит. */
  onGone: () => void;
}

/**
 * Раскрытая запись «Недавно добавленных» — разрез (ТЗ главной § 22.5): строка над разделами (транскрипция
 * и строка о жетоне), разделы «Переводы» / «Слова» / «Фразы» / «Фразовые глаголы» / «Не созданы»
 * плотными строками без рамок и без подсказки, порции, низ записи. Состав — `GET /generations/{id}`
 * при первом раскрытии; пока ждём — скелетон той же раскладки.
 */
export function GenerationBreakdown({ summary, now, itemSlowSince, ctx, onCollapse, onGone }: GenerationBreakdownProps) {
  const { t } = useTranslation();
  const query = useGenerationQuery(summary.id);
  const detail = query.data;
  const gone = query.error instanceof GenerationNotFoundError;

  useEffect(() => {
    if (!gone) return;
    const timer = window.setTimeout(onGone, GONE_DELAY_MS);
    return () => window.clearTimeout(timer);
    // onGone — колбэк родителя для этой записи; таймер — один на 404.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gone]);

  // Ошибки состава объявляются: это ответ на раскрытие записи учеником (исключение к § 22.11, «секция не live»).
  if (gone) {
    return (
      <div className="gl-body">
        <p role="status" className="text-[14px] leading-5 text-muted">
          {t('generations.detail.gone')}
        </p>
      </div>
    );
  }
  if (!detail) {
    if (query.isError) {
      return (
        <div className="gl-body">
          <div className="flex flex-wrap items-center gap-x-3">
            <p role="alert" className="text-[14px] leading-5 text-muted">
              {t('generations.detail.error')}
            </p>
            <button type="button" className="gl-text gl-text--accent" onClick={() => void query.refetch()}>
              {t('generations.retry')}
            </button>
          </div>
        </div>
      );
    }
    return <BreakdownSkeleton />;
  }
  return <BreakdownContent detail={detail} now={now} itemSlowSince={itemSlowSince} ctx={ctx} onCollapse={onCollapse} />;
}

function BreakdownSkeleton() {
  return (
    <div className="gl-body" aria-busy>
      <Bar width="120px" />
      <div className="gl-skel mt-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="gl-skel-row">
            <Bar width="40%" />
            <Bar width="70%" />
            <Bar width="55%" />
          </div>
        ))}
      </div>
    </div>
  );
}

function BreakdownContent({
  detail,
  now,
  itemSlowSince,
  ctx,
  onCollapse,
}: {
  detail: GenerationDetail;
  now: number;
  itemSlowSince: (itemId: string) => number | null;
  ctx: ItemActionsContext;
  onCollapse: () => void;
}) {
  const { t } = useTranslation();
  const breakdown = useMemo(() => buildBreakdown(detail), [detail]);
  const token = tokenLine(detail);
  return (
    <div className="gl-body">
      {(breakdown.transcription || token) && (
        <div className="gl-top">
          {breakdown.transcription && <span className="gl-ipa">{breakdown.transcription}</span>}
          {token && (
            <span className="gl-token">
              <TokenIcon size={14} className="mt-[3px]" />
              <span>{'count' in token ? t(token.key, { count: token.count }) : t(token.key)}</span>
            </span>
          )}
        </div>
      )}
      <div className="gl-secs">
        {breakdown.sections.map((section) => (
          <SectionView key={section.kind} section={section} detail={detail} now={now} itemSlowSince={itemSlowSince} ctx={ctx} />
        ))}
      </div>
      <div className="gl-foot">
        {detail.module ? (
          <Link
            to={buildUnitPath(detail.module.id)}
            className="gl-act gl-act--tall"
            onClick={() => trackEvent('generation_action', { surface: ctx.surface, action: 'open_module', kind: detail.kind })}
          >
            <span className="gl-act-face">{t('generations.action.openModule')}</span>
          </Link>
        ) : (
          <span />
        )}
        <button type="button" className="gl-text" onClick={onCollapse}>
          {t('generations.action.collapse')}
        </button>
      </div>
    </div>
  );
}

function SectionView({
  section,
  detail,
  now,
  itemSlowSince,
  ctx,
}: {
  section: BreakdownSection;
  detail: GenerationDetail;
  now: number;
  itemSlowSince: (itemId: string) => number | null;
  ctx: ItemActionsContext;
}) {
  const { t } = useTranslation();
  const labelId = useId();
  const win = section.kind === 'translations' ? SENSES_WINDOW : section.kind === 'skipped' ? SKIPPED_WINDOW : ITEMS_WINDOW;
  const [limit, setLimit] = useState(win.base);
  // Строка, которую порция показала из-за статуса, остаётся на месте и после его смены («Повторить» у
  // элемента за порцией, дописанное значение): иначе она пропала бы из-под пальца, а высота записи прыгнула.
  // «Скрыть» возвращает обычную порцию.
  const held = useRef(new Set<string>());
  const collapse = () => {
    held.current.clear();
    setLimit(win.base);
  };
  const label =
    section.kind === 'translations'
      ? t('generations.section.translations')
      : t(`generations.section.${section.kind}`, { count: section.count });
  // Фразы, фразовые глаголы и слова подборки — «что» начинается с самой фразы; у значений слова записи
  // «что» — переводы, а само слово уже в заголовке записи.
  const headWord = section.kind !== 'translations';

  /** Строки элемента: строка элемента со статусом или строки значений (`senses` — видимая порция). */
  const renderItem = (entry: BreakdownItem, senses: BreakdownItem['senses'] = entry.senses): ReactNode[] => {
    const view = itemStatusView(entry.item, { now, slowSince: itemSlowSince(entry.item.id) });
    if (view.itemRow) {
      return [<ItemLine key={`${entry.item.id}:${entry.item.status}`} detail={detail} entry={entry} view={view} headWord={headWord} ctx={ctx} />];
    }
    // Статус идущего элемента — в колонке «пример» первой идущей строки; «Ввести снова» — справа в первой
    // недобравшейся.
    const firstPending = view.label ? senses.findIndex((s) => s.status === 'generating') : -1;
    const firstFailed = view.action === 'reenter' ? senses.findIndex((s) => s.status === 'failed') : -1;
    return senses.map((sense, index) => (
      <SenseLine
        key={`${sense.key}:${sense.status}`}
        sense={sense}
        word={entry.item.frontName}
        // Часть речи — у значений слова; у фраз и фразовых глаголов её не подписываем.
        showPos={entry.item.kind === 'word'}
        head={headWord && index === 0 ? entry.item.frontName : null}
        status={index === firstPending && view.label ? { label: t(view.label.key, view.label.params), tone: view.tone, ring: view.ring } : null}
        action={index === firstFailed ? <ReenterButton detail={detail} entry={entry} ctx={ctx} /> : undefined}
      />
    ));
  };

  const pagerButton = (text: string, onClick: () => void) => (
    <button type="button" className="gl-text -ml-1.5" onClick={onClick}>
      {text}
    </button>
  );
  // Свернуть порцию — «Скрыть», а не «Свернуть»: так называется кнопка всей записи внизу, и диктор слышал две
  // одинаковые кнопки с разными действиями (приёмка A2-06). «ещё N» — общий текст с листом словаря.
  const pagerFor = (paged: { more: number; canCollapse: boolean }, more: () => void): ReactNode => {
    if (paged.more > 0) return pagerButton(t('cardsWorkspace.pick.more', { n: paged.more }), more);
    if (paged.canCollapse) return pagerButton(t('generations.action.fewerItems'), collapse);
    return null;
  };
  /** Порция с удержанными строками: строки со статусом не прячутся, однажды показанные — остаются. */
  const keepWith =
    <T,>(keyOf: (row: T) => string, needsEye: (row: T) => boolean) =>
    (row: T, index: number) => {
      const key = keyOf(row);
      if (held.current.has(key)) return true;
      if (index < limit || !needsEye(row)) return false;
      held.current.add(key);
      return true;
    };

  let rows: ReactNode[];
  let pager: ReactNode = null;
  if (section.kind === 'translations') {
    // «Переводы» — строки значений слова порциями: 3, затем все остальные; строка элемента — первой.
    const lines = section.items.flatMap((entry) => entry.senses.map((sense) => ({ entry, sense })));
    const paged = pageList(
      lines,
      limit,
      win,
      keepWith(
        (line) => line.sense.key,
        (line) => senseNeedsEye(line.entry, line.sense),
      ),
    );
    const shown = new Set(paged.visible.map((line) => line.sense.key));
    rows = section.items.flatMap((entry) => renderItem(entry, entry.senses.filter((sense) => shown.has(sense.key))));
    if (paged.more > 0) {
      pager = pagerButton(t('generations.action.moreSenses', { count: paged.more }), () => {
        trackEvent('generation_action', { surface: ctx.surface, action: 'more_senses', kind: detail.kind });
        setLimit(paged.next);
      });
    } else if (paged.canCollapse) {
      pager = pagerButton(t('generations.action.fewer'), collapse);
    }
  } else if (section.kind === 'skipped') {
    const paged = pageList(section.items, limit, win);
    rows = paged.visible.map(({ item }) => (
      <div key={item.id} className="gl-r text-muted">
        <span lang="en" className="gl-skip-word">
          {item.frontName}
        </span>{' '}
        — {t(skipReasonKey(item.skipReason))}
      </div>
    ));
    pager = pagerFor(paged, () => setLimit(paged.next));
  } else {
    const paged = pageList(
      section.items,
      limit,
      win,
      keepWith((entry) => entry.item.id, itemNeedsEye),
    );
    rows = paged.visible.flatMap((entry) => renderItem(entry));
    pager = pagerFor(paged, () => {
      trackEvent('generation_action', { surface: ctx.surface, action: 'more_items', kind: detail.kind });
      setLimit(paged.next);
    });
  }

  return (
    <div role="group" aria-labelledby={labelId} className="gl-sec">
      <div id={labelId} className="gl-sec-label">
        {label}
      </div>
      <div className="min-w-0">
        {rows}
        {pager}
        {/* По самой карточке, а не по виду записи: «Добавить вручную» может привязать готовую карточку
            слова из другого модуля — тогда у строк есть и пример, и смысл. */}
        {section.kind === 'translations' && section.items.some((entry) => entry.item.manual) && (
          <p className="gl-sub pb-1.5">{t('generations.manualNote')}</p>
        )}
      </div>
    </div>
  );
}
